<template>
    <BlockContainer
        :id="'template-gallery-' + slugify(data.title)"
        :background-color="data.backgroundColor"
        :bottom-angle="data.bottomAngle"
        :angle-background-color="data.nextBackgroundColor"
    >
        <div class="pb-24 pt-14">
            <ais-instant-search-ssr>
                <ais-configure :hits-per-page.camel="1000" />

                <div class="gap-x-8 lg:flex">
                    <div class="flex-none">
                        <div
                            class="btn-primary flex items-center justify-center gap-x-2 lg:hidden lg:flex-none"
                            @click="mobileMenuOpen = true"
                        >
                            Filter
                            <Icon name="bars-filter-solid" class="text-xl" />
                        </div>
                        <transition name="slide-up">
                            <div
                                v-show="mobileMenuOpen"
                                class="fixed inset-x-0 top-0 z-[999999] h-screen overflow-y-scroll bg-white px-4 py-4 lg:hidden lg:flex-none"
                            >
                                <div class="mb-4 flex justify-between">
                                    <h3 class="text-xl">Product Filter</h3>
                                    <Icon
                                        name="xmark-solid"
                                        class="text-xl"
                                        @click="mobileMenuOpen = false"
                                    />
                                </div>
                                <TemplateFiltersLists
                                    :mobile-menu-open="mobileMenuOpen"
                                />
                                <div class="mt-4 flex">
                                    <div
                                        class="btn-primary w-full text-center"
                                        @click="mobileMenuOpen = false"
                                    >
                                        <ais-stats>
                                            <template #default="{ nbHits }">
                                                <div class="text-white">
                                                    View {{ nbHits }}
                                                    <span v-if="nbHits > 1"
                                                        >results</span
                                                    ><span v-else>result</span>
                                                </div>
                                            </template>
                                        </ais-stats>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                    <aside class="flex-none lg:top-32 lg:w-1/4 lg:self-start">
                        <div class="hidden lg:block">
                            <TemplateFiltersLists
                                :mobile-menu-open="mobileMenuOpen"
                            />
                        </div>
                    </aside>
                    <div class="mt-8 flex-1 lg:mt-0">
                        <div class="w-full lg:min-h-[44px]">
                            <ais-current-refinements
                                class="mb-4 mt-0 flex gap-2"
                            >
                                <template #default="{ items, createURL }">
                                    <template
                                        v-for="item in items"
                                        :key="item.attribute"
                                    >
                                        <div
                                            v-for="refinement in item.refinements"
                                            :key="
                                                [
                                                    refinement.attribute,
                                                    refinement.type,
                                                    refinement.value,
                                                    refinement.operator,
                                                ].join(':')
                                            "
                                            class="rounded-md bg-white shadow-md hover:bg-gray-100"
                                        >
                                            <a
                                                :href="createURL(refinement)"
                                                class="flex items-center gap-2 px-5 py-3 text-xs uppercase text-grind no-underline"
                                                @click.prevent="
                                                    item.refine(refinement)
                                                "
                                            >
                                                {{ refinement.label }}
                                                <Icon
                                                    name="filter-x"
                                                    class="text-xs text-grind"
                                                />
                                            </a>
                                        </div>
                                    </template>
                                </template>
                            </ais-current-refinements>
                        </div>
                        <ais-infinite-hits>
                            <template #default="{ items }">
                                <TemplateProductGroup
                                    :template-groups="templateGroups"
                                    :materials="items"
                                />
                            </template>
                        </ais-infinite-hits>
                        <div class="invisible">
                            <!-- This exists solely for Google to crawl, hence why it's invisible to the user: -->
                            <ais-pagination></ais-pagination>
                        </div>
                    </div>
                </div>
            </ais-instant-search-ssr>
        </div>
    </BlockContainer>
</template>

<script lang="ts" setup>
import TemplateGalleryType from '~/types/TemplateGalleryType';
import TemplateGroupType from '~/types/TemplateGroupType';
import BlockContainer from '@/components/BlockContainer.vue';
import {
    AisConfigure,
    AisCurrentRefinements,
    AisInfiniteHits,
    AisInstantSearchSsr,
    AisRefinementList,
    AisStats,
    AisPagination,
    createServerRootMixin,
} from 'vue-instantsearch/vue3/es';
import { ref } from 'vue';
import { renderToString } from '@vue/server-renderer';
import { useRuntimeConfig } from '#app/nuxt';
import TemplateFiltersLists from '~/components/page-building/template-gallery/components/TemplateFiltersLists.vue';
import TemplateProductGroup from '~/components/page-building/template-gallery/components/TemplateProductGroup.vue';
import { h } from 'vue';

defineProps<{
    data: TemplateGalleryType;
    templateGroups: TemplateGroupType[];
}>();

const algoliaTemplatesAndGuidesIndex = computed<string>(() => {
    return useRuntimeConfig().public.algoliaTemplatesAndGuidesIndex;
});

const algoliaIndex = computed<string>(() => {
    return algoliaTemplatesAndGuidesIndex.value;
});

const indexName = algoliaIndex.value;
const initialPage = 0;
const mobileMenuOpen = ref(false);
const algolia = useAlgoliaRef();
const serverRootMixin = ref(
    createServerRootMixin({
        searchClient: algolia,
        indexName,
        initialUiState: {
            [algoliaIndex.value]: {
                configure: {
                    hitsPerPage: 1000,
                    page: initialPage,
                },
                refinementList: {},
            },
        },
    }),
);

const { instantsearch } = serverRootMixin.value.data();
provide('$_ais_ssrInstantSearchInstance', instantsearch);

const { data: algoliaState } = await useAsyncData('algolia-state', async () => {
    if (process.server) {
        const nuxtApp = useNuxtApp();
        nuxtApp.$algolia.transporter.requester = (
            await import('@algolia/requester-node-http').then(
                (lib) => lib.default || lib,
            )
        ).createNodeHttpRequester();
    }
    return instantsearch.findResultsState({
        // IMPORTANT: a component with access to `this.instantsearch` to be used by the createServerRootMixin code
        component: {
            $options: {
                components: {
                    AisInstantSearchSsr,
                    AisConfigure,
                    AisRefinementList,
                    AisCurrentRefinements,
                    AisInfiniteHits,
                },
                data() {
                    return { instantsearch };
                },
                provide: { $_ais_ssrInstantSearchInstance: instantsearch },
                render() {
                    return h(AisInstantSearchSsr, null, () => [
                        // Include any vue-instantsearch components that you use including each refinement attribute
                        h(AisInfiniteHits),
                        h(AisCurrentRefinements),
                        h(AisConfigure, {
                            hitsPerPage: 1000,
                            maxValuesPerFacet: 100,
                        }),
                        h(AisRefinementList, {
                            attribute: 'product_type',
                            operator: 'or',
                        }),
                        h(AisRefinementList, {
                            attribute: 'material',
                            operator: 'or',
                        }),
                    ]);
                },
            },
        },
        renderToString,
    });
});
if (process.client) {
    instantsearch.hydrate(algoliaState.value);
}
</script>
